import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { settingsAPI, userAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaMapMarkerAlt, FaCamera, FaMicrophone, FaBell, FaCog, FaUser, FaSignOutAlt } from 'react-icons/fa';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../context/AuthContext';
import { getProfilePicture, getUploadUrl } from '../utils/helpers';

// Ülke listesi
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia',
  'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'East Timor', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Ethiopia',
  'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
  'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India',
  'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan',
  'Kazakhstan', 'Kenya', 'Kiribati', 'North Korea', 'South Korea', 'Kuwait', 'Kyrgyzstan', 'Laos',
  'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
  'Macedonia', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
  'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro',
  'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand',
  'Nicaragua', 'Niger', 'Nigeria', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
  'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa',
  'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Swaziland', 'Sweden', 'Switzerland',
  'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago',
  'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates',
  'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
  'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();

  // Debug user changes
  useEffect(() => {
    console.log('👤 Settings Header - User changed:', user?.id, user?.profile_photo, user?.full_name);
  }, [user]);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const { permissions, requestBrowserPermission, loading: permissionsLoading } = usePermissions();

  const logout = async () => {
    try {
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('Settings: Logout error:', error);
    }
  };
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    bio: '',
    interests: '',
    height: '',
    weight: '',
    city: '',
    country: '',
    instagram_username: '',
    facebook_username: '',
    telegram_username: '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [preferences, setPreferences] = useState({
    sexual_preference: '3',
    sexual_orientation: '1',
    age_min: 18,
    age_max: 100,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.getSettings();
      console.log('Settings response:', response);

      if (response.profile) {
        setProfileData({
          full_name: response.profile.full_name || '',
          email: response.profile.email || '',
          bio: response.profile.bio || '',
          interests: response.profile.interests || '',
          height: response.profile.height || '',
          weight: response.profile.weight || '',
          instagram_username: response.profile.instagram_username || '',
          facebook_username: response.profile.facebook_username || '',
          telegram_username: response.profile.telegram_username || '',
          city: response.profile.city || '',
          country: response.profile.country || '',
        });

        console.log('📱 Settings - Social media data from API:', {
          instagram: response.profile.instagram_username,
          facebook: response.profile.facebook_username,
          telegram: response.profile.telegram_username
        });
      }

      if (response.preferences) {
        setPreferences({
          sexual_preference: response.preferences.sexual_preference || '3',
          sexual_orientation: response.preferences.sexual_orientation || '1',
          age_min: response.preferences.age_min || 18,
          age_max: response.preferences.age_max || 100,
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    console.log('💾 Saving profile data with social media:', {
      instagram_username: profileData.instagram_username,
      facebook_username: profileData.facebook_username,
      telegram_username: profileData.telegram_username
    });
    try {
      setLoading(true);
      await userAPI.updateProfile(profileData);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      setLoading(true);
      await settingsAPI.updatePassword(passwordData);
      toast.success('Password updated successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error('Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferencesUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await settingsAPI.updatePreferences(preferences);
      toast.success('Preferences updated successfully!');
    } catch (error) {
      toast.error('Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="matchus-settings min-h-screen bg-gray-50">
      {/* Header */}
      <header className="matchus-encounters-header" style={{ height: 'auto' }}>
        <div className="matchus-header-container">
          <div className="matchus-header-left">
            <Link to="/encounters" className="matchus-logo">
              <img src="/assets/images/logo-matchus.svg" alt="MatchUs" className="h-6 w-6 mr-2" />
              <span className="matchus-brand-name">Match Us</span>
            </Link>
          </div>

          <div className="matchus-header-center">
            <h1 className="matchus-page-title">Settings</h1>
          </div>

          <div className="matchus-header-right">
            <button className="matchus-header-btn" title="Notifications">
              <FaBell />
              <span className="matchus-notification-badge">3</span>
            </button>

            <div className="matchus-user-menu">
              <button 
                className="matchus-user-btn"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
              >
                <img
                  key={user?.id || 'guest'}
                  src={user?.profile_photo ? getUploadUrl(`user-${user.id}/${user.profile_photo}`) : getProfilePicture(user || { id: 1 })}
                  alt="Profile"
                  className="matchus-user-avatar"
                />
                <span className="matchus-user-name">{user?.full_name?.split(' ')[0] || 'You'}</span>
              </button>

              <div className={`matchus-user-dropdown ${showUserDropdown ? 'show' : ''}`}>
                <Link to="/settings" className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                  <FaCog />
                  <span>Settings</span>
                </Link>
                <Link to={`/user/${user?.id}`} className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                  <FaUser />
                  <span>Profile</span>
                </Link>
                <div className="matchus-dropdown-divider"></div>
                {(user?.is_admin === 1 || user?.role?.name === 'admin' || user?.role?.name === 'moderator') && (
                  <Link to="/admin" className="matchus-dropdown-item" onClick={() => setShowUserDropdown(false)}>
                    <FaCog />
                    <span>Admin Panel</span>
                  </Link>
                )}
                <button
                  type="button"
                  className="matchus-dropdown-logout"
                  onClick={() => {
                    setShowUserDropdown(false);
                    logout(() => navigate('/?login'));
                  }}
                >
                  <FaSignOutAlt />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Overlay to close dropdown */}
        {showUserDropdown && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowUserDropdown(false)}
          />
        )}

        {/* Tab Navigation in Header */}
        <div className="matchus-header-tabs">
          {/* Mobile Tab Navigation */}
          <div className="lg:hidden px-3 py-3">
            <div className="flex space-x-1 bg-gray-100 sm:bg-gray-200 p-1 rounded-lg overflow-x-auto">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex-1 min-w-0 py-2.5 px-3 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'profile'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`flex-1 min-w-0 py-2.5 px-3 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'password'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Password
              </button>
              <button
                onClick={() => setActiveTab('preferences')}
                className={`flex-1 min-w-0 py-2.5 px-3 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'preferences'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Preferences
              </button>
              <button
                onClick={() => setActiveTab('permissions')}
                className={`flex-1 min-w-0 py-2.5 px-3 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === 'permissions'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Permissions
              </button>
            </div>
          </div>

          {/* Desktop Tab Navigation */}
          <div className="hidden lg:block px-6 py-3">
            <nav className="flex flex-wrap justify-center gap-4 sm:gap-6 lg:gap-8 border-b border-gray-200 pb-2">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-2 px-2 sm:px-3 border-b-2 font-medium text-sm sm:text-base transition-colors whitespace-nowrap ${
                  activeTab === 'profile'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`py-2 px-2 sm:px-3 border-b-2 font-medium text-sm sm:text-base transition-colors whitespace-nowrap ${
                  activeTab === 'password'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Password
              </button>
              <button
                onClick={() => setActiveTab('preferences')}
                className={`py-2 px-2 sm:px-3 border-b-2 font-medium text-sm sm:text-base transition-colors whitespace-nowrap ${
                  activeTab === 'preferences'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Preferences
              </button>
              <button
                onClick={() => setActiveTab('permissions')}
                className={`py-2 px-2 sm:px-3 border-b-2 font-medium text-sm sm:text-base transition-colors whitespace-nowrap ${
                  activeTab === 'permissions'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Permissions
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="matchus-main-content">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">


        {/* Tab Content */}
        <div className="space-y-4 sm:space-y-6">
          {/* Profile Tab */}
                {activeTab === 'profile' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Profile Information</h2>
                <p className="text-gray-600 text-sm">Update your personal information and profile details</p>
              </div>

              <form onSubmit={handleProfileUpdate} className="space-y-4 sm:space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                          <input
                            type="text"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            value={profileData.full_name}
                            onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                          />
                        </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                          <input
                            type="email"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            value={profileData.email}
                            onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                          />
                        </div>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    About Me
                  </label>
                          <textarea
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-vertical transition-colors"
                            rows="4"
                    placeholder="Tell others about yourself..."
                            value={profileData.bio}
                            onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                          />
                        </div>

                {/* Interests */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Interests (comma separated)
                  </label>
                          <input
                            type="text"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                    placeholder="e.g. Music, Travel, Sports, Movies"
                            value={profileData.interests || ''}
                            onChange={(e) => setProfileData({ ...profileData, interests: e.target.value })}
                          />
                        </div>

                        {/* Social Media */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Instagram Username
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                              placeholder="@yourusername"
                              value={profileData.instagram_username || ''}
                              onChange={(e) => setProfileData({ ...profileData, instagram_username: e.target.value })}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Facebook Username
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                              placeholder="yourusername"
                              value={profileData.facebook_username || ''}
                              onChange={(e) => setProfileData({ ...profileData, facebook_username: e.target.value })}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Telegram Username
                            </label>
                            <input
                              type="text"
                              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                              placeholder="@yourusername"
                              value={profileData.telegram_username || ''}
                              onChange={(e) => setProfileData({ ...profileData, telegram_username: e.target.value })}
                            />
                          </div>
                        </div>

                {/* Location */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country
                    </label>
                              <select
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                                value={profileData.country || ''}
                                onChange={(e) => setProfileData({ ...profileData, country: e.target.value })}
                              >
                                <option value="">Select Country</option>
                                {COUNTRIES.map((country) => (
                                  <option key={country} value={country}>
                                    {country}
                                  </option>
                                ))}
                              </select>
                            </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                              <input
                                type="text"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                      placeholder="e.g. Istanbul"
                                value={profileData.city || ''}
                                onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                              />
                            </div>
                          </div>

                {/* Physical Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Height (cm)
                    </label>
                              <input
                                type="number"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                      placeholder="170"
                                value={profileData.height || ''}
                                onChange={(e) => setProfileData({ ...profileData, height: e.target.value })}
                              />
                            </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Weight (kg)
                    </label>
                              <input
                                type="number"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                      placeholder="70"
                                value={profileData.weight || ''}
                                onChange={(e) => setProfileData({ ...profileData, weight: e.target.value })}
                              />
                            </div>
                          </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    className="bg-pink-500 hover:bg-pink-600 text-white font-medium py-2.5 px-6 sm:px-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    disabled={loading}
                  >
                          {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                </div>
                      </form>
                  </div>
                )}

          {/* Password Tab */}
                {activeTab === 'password' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Change Password</h2>
                <p className="text-gray-600 text-sm">Update your password to keep your account secure</p>
              </div>

              <form onSubmit={handlePasswordUpdate} className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                          <input
                            type="password"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                            required
                          />
                        </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password
                    </label>
                          <input
                            type="password"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            value={passwordData.new_password}
                            onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                            required
                          />
                        </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm New Password
                    </label>
                          <input
                            type="password"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                            required
                          />
                        </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    className="bg-pink-500 hover:bg-pink-600 text-white font-medium py-2.5 px-6 sm:px-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    disabled={loading}
                  >
                          {loading ? 'Updating...' : 'Update Password'}
                        </button>
                </div>
                      </form>
                  </div>
                )}

          {/* Preferences Tab */}
                {activeTab === 'preferences' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Dating Preferences</h2>
                <p className="text-gray-600 text-sm">Set your preferences to find better matches</p>
              </div>

              <form onSubmit={handlePreferencesUpdate} className="space-y-4 sm:space-y-6">
                {/* Interested In */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      I'm interested in
                    </label>
                          <select
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            value={preferences.sexual_preference}
                      onChange={(e) => setPreferences({ ...preferences, sexual_preference: e.target.value })}
                          >
                      <option value="1">Men</option>
                      <option value="2">Women</option>
                      <option value="3">Everyone</option>
                          </select>
                        </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      My sexual orientation
                    </label>
                          <select
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                            value={preferences.sexual_orientation}
                      onChange={(e) => setPreferences({ ...preferences, sexual_orientation: e.target.value })}
                          >
                            <option value="1">Straight</option>
                            <option value="2">Gay</option>
                            <option value="3">Lesbian</option>
                            <option value="4">Bisexual</option>
                          </select>
                        </div>
                </div>

                {/* Age Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Age Range
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Minimum Age</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                        placeholder="18"
                        min="18"
                        max="99"
                        value={preferences.age_min}
                        onChange={(e) => setPreferences({ ...preferences, age_min: parseInt(e.target.value) || 18 })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Maximum Age</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                        placeholder="100"
                        min="19"
                        max="100"
                        value={preferences.age_max}
                        onChange={(e) => setPreferences({ ...preferences, age_max: parseInt(e.target.value) || 100 })}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Current range: {preferences.age_min} - {preferences.age_max} years old
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    className="bg-pink-500 hover:bg-pink-600 text-white font-medium py-2.5 px-6 sm:px-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Preferences'}
                  </button>
              </div>
              </form>
            </div>
          )}

          {/* Permissions Tab */}
          {activeTab === 'permissions' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">App Permissions</h2>
                <p className="text-gray-600 text-sm">Manage permissions for location, camera, microphone, and notifications to enhance your MatchUs experience.</p>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {/* Location Permission */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 rounded-lg gap-3 sm:gap-4">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="flex-shrink-0">
                      <FaMapMarkerAlt className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">Location Access</h4>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">Allow access to your location for better matching and nearby people discovery</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end space-x-3 sm:flex-shrink-0">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      permissions.location?.granted
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {permissions.location?.granted ? 'Granted' : 'Not Granted'}
                    </span>
                    <button
                      onClick={() => requestBrowserPermission('location')}
                      disabled={permissionsLoading}
                      className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md whitespace-nowrap ${
                        permissions.location?.granted
                          ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                          : 'text-white bg-pink-600 hover:bg-pink-700'
                      } disabled:opacity-50`}
                    >
                      {permissions.location?.granted ? 'Re-request' : 'Request'}
                    </button>
                  </div>
                </div>

                {/* Camera Permission */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 rounded-lg gap-3 sm:gap-4">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="flex-shrink-0">
                      <FaCamera className="h-5 w-5 sm:h-6 sm:w-6 text-green-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">Camera Access</h4>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">Allow camera access for taking photos and video calls</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end space-x-3 sm:flex-shrink-0">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      permissions.camera?.granted
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {permissions.camera?.granted ? 'Granted' : 'Not Granted'}
                    </span>
                    <button
                      onClick={() => requestBrowserPermission('camera')}
                      disabled={permissionsLoading}
                      className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md whitespace-nowrap ${
                        permissions.camera?.granted
                          ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                          : 'text-white bg-pink-600 hover:bg-pink-700'
                      } disabled:opacity-50`}
                    >
                      {permissions.camera?.granted ? 'Re-request' : 'Request'}
                    </button>
                  </div>
                </div>

                {/* Microphone Permission */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 rounded-lg gap-3 sm:gap-4">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="flex-shrink-0">
                      <FaMicrophone className="h-5 w-5 sm:h-6 sm:w-6 text-pink-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">Microphone Access</h4>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">Allow microphone access for voice messages and video calls</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end space-x-3 sm:flex-shrink-0">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      permissions.microphone?.granted
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {permissions.microphone?.granted ? 'Granted' : 'Not Granted'}
                    </span>
                    <button
                      onClick={() => requestBrowserPermission('microphone')}
                      disabled={permissionsLoading}
                      className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md whitespace-nowrap ${
                        permissions.microphone?.granted
                          ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                          : 'text-white bg-pink-600 hover:bg-pink-700'
                      } disabled:opacity-50`}
                    >
                      {permissions.microphone?.granted ? 'Re-request' : 'Request'}
                    </button>
                  </div>
                </div>

                {/* Notifications Permission */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 rounded-lg gap-3 sm:gap-4">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="flex-shrink-0">
                      <FaBell className="h-5 w-5 sm:h-6 sm:w-6 text-orange-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">Push Notifications</h4>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">Receive notifications about new matches, messages, and app updates</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end space-x-3 sm:flex-shrink-0">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                      permissions.notifications?.granted
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {permissions.notifications?.granted ? 'Granted' : 'Not Granted'}
                    </span>
                    <button
                      onClick={() => requestBrowserPermission('notifications')}
                      disabled={permissionsLoading}
                      className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md whitespace-nowrap ${
                        permissions.notifications?.granted
                          ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                          : 'text-white bg-pink-600 hover:bg-pink-700'
                      } disabled:opacity-50`}
                    >
                      {permissions.notifications?.granted ? 'Re-request' : 'Request'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 sm:mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <FaBell className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400 mt-0.5" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">Privacy & Security</h4>
                    <p className="mt-1 text-xs sm:text-sm text-blue-700">
                      Your permissions are stored securely and used only to enhance your experience.
                      You can revoke permissions at any time through your browser settings.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;